// Clouds.tsx — VOLUMETRIC cloud shell. Earlier this was a thin sphere whose hard
// silhouette read as a sharp painted edge at the limb. Here the deck is a finite-
// thickness spherical SHELL [innerR, outerR] that the fragment shader ray-marches:
// a view ray accumulates cloud density + sun lighting through the shell, so grazing
// limb rays — which traverse a long tangential slice of the layer — build up a soft,
// thick band that fades into the atmosphere instead of a knife edge, while face-on
// rays cross only the thin layer. The bank field (drift / belts / storm vortices /
// coverage) is the same flow as before, now evaluated per marched sample in the
// deck's local frame so it still rides with the planet. Only mounted for the focused
// planet (the march is per-pixel) and lit by the star with the shared eclipse term.
import { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Color, Vector3 } from 'three';
import type { DataTexture, Mesh } from 'three';
import { SHADOW_PARS, makeShadowUniforms, setOccluders } from './shadow';
import type { Occluder } from './shadow';
import { FBM3D, SIMPLEX3D } from './planet/glslNoise';
import { CLIMATE_PARS } from './planet/climateGlsl';
import { CLOUD_COVERAGE_SCALE, cloudThresholds } from './planet/cloudField';
import type { SurfaceClimate } from './planet/surfaceShader';
import { gameClock } from '../state/gameClock';

// Number of standing storm vortices per planet (GLSL array size === JS uniform size).
const NVORTEX = 3;
// Ray-march steps through the shell. Modest — only the focused planet marches, and
// the jittered accumulation + altitude falloff hide the step count.
const MARCH_STEPS = 14;
// Shell geometry: the deck spans innerR (just above the surface) to outerR. The mesh
// geometry is the OUTER sphere so fragments — and thus the soft limb — extend to it.
const INNER_SCALE = 1.004;
const OUTER_SCALE = 1.07;

const vert = /* glsl */ `
  varying vec3 vLocalPos;
  varying vec3 vWorldPos;
  void main() {
    vLocalPos = position;            // on the outer shell, in the deck's local frame
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

const frag = /* glsl */ `
  precision highp float;
  #define PI 3.14159265359
  #define NVORTEX ${NVORTEX}
  #define STEPS ${MARCH_STEPS}
  uniform float uTime;
  uniform vec3 uColor;
  uniform float uCoverLo;
  uniform float uCoverHi;
  uniform float uFreq;
  uniform float uExtinction;           // optical density (higher = more opaque)
  uniform float uOpacity;              // final alpha scale
  uniform vec3 uDriftAxis;
  uniform float uDriftSpeed;
  uniform float uPhase;
  uniform float uBaseFlow;
  uniform float uBandCount;
  uniform float uBandPhase;
  uniform vec3 uVortexCenter[NVORTEX];
  uniform vec3 uVortexData[NVORTEX];   // x: angular radius, y: spiral twist, z: spin rate
  uniform vec3 uSunLocalDir;           // sun direction in the deck's local frame
  uniform vec3 uCamLocal;              // camera position in the deck's local frame
  uniform float uInnerR;               // shell radii, local units
  uniform float uOuterR;
  uniform float uClearOnHeat;          // 1 = clear the deck over hot/dry (scorched) cells
  uniform sampler2D uWeather;          // baked seasonal cloud cover (the orbital month-pair lerp)
  uniform float uHasWeather;           // 1 once the baked weather video has loaded
  varying vec3 vLocalPos;
  varying vec3 vWorldPos;
  ${SHADOW_PARS}
  ${SIMPLEX3D}
  ${FBM3D}
  ${CLIMATE_PARS}

  // Rotation about an arbitrary unit axis (Rodrigues) — per-planet spin + storm swirl.
  mat3 rotAxis(vec3 a, float ang){
    float c = cos(ang), s = sin(ang), t = 1.0 - c;
    return mat3(
      t*a.x*a.x + c,     t*a.x*a.y - s*a.z, t*a.x*a.z + s*a.y,
      t*a.x*a.y + s*a.z, t*a.y*a.y + c,     t*a.y*a.z - s*a.x,
      t*a.x*a.z - s*a.y, t*a.y*a.z + s*a.x, t*a.z*a.z + c
    );
  }

  // The flowing bank frame: rigid zonal drift + bounded latitude belts + standing
  // storm vortices (cheap rotations — no per-sample fBm, so the march stays affordable).
  vec3 cloudFlow(vec3 d0){
    vec3 axis = normalize(uDriftAxis);
    // Rotating worlds get rigid zonal drift + Coriolis latitude belts. A tidally-locked world does
    // NOT spin, so it has neither — its circulation is anchored to the fixed substellar point.
    // rot=0 there kills the drift and belts, leaving the deck stationary (only the slow vortex
    // churn) so the substellar weather gate organises it into an eyeball ring rather than smearing
    // it into drifting Earth-like bands.
    float rot = 1.0 - uTidalLock;
    vec3 dir = rotAxis(axis, uTime * uDriftSpeed * uBaseFlow * rot) * d0;
    float lat = asin(clamp(dot(dir, axis), -1.0, 1.0));
    float band = 0.13 * rot * cos(lat * uBandCount + uBandPhase + uTime * uDriftSpeed * 1.5);
    dir = rotAxis(axis, band) * dir;
    for (int i = 0; i < NVORTEX; i++) {
      vec3 c = uVortexCenter[i];
      float r0 = uVortexData[i].x;
      float g = acos(clamp(dot(d0, c), -1.0, 1.0));
      float wv = exp(-(g * g) / (r0 * r0));
      float ang = uVortexData[i].y * wv * (0.75 + 0.25 * sin(uTime * uVortexData[i].z));
      dir = rotAxis(c, ang) * dir;
    }
    return dir;
  }

  // 3-octave fBm — cheaper than the shared 5-octave FBM3D (fewer snoise per call), which
  // matters because it runs at every march step; the marching itself supplies the higher-
  // frequency 3D structure, so the lost octaves aren't missed.
  float cfbm(vec3 p){
    float s = 0.0, a = 0.5, tot = 0.0;
    for (int i = 0; i < 3; i++) { s += a * snoise(p); p *= 2.03; tot += a; a *= 0.5; }
    return (s / tot) * 0.5 + 0.5;
  }

  // Vertical profile: a flat-ish condensation base (soft bottom) rising to a lumpy top
  // at altitude "top". Crucially the top VARIES per column (tall convective piles where
  // the field is strong, thin flats elsewhere) so the deck is never a uniform-height slab.
  float vProfile(float alt, float top){
    return smoothstep(0.0, 0.08, alt) * (1.0 - smoothstep(top * 0.5, top, alt));
  }

  // Equirect UV for a local-frame direction, matching the server's baked-map layout exactly
  // (lon = atan2(z,x) -> u, lat = asin(y) with row 0 = +90° -> v). The deck rides in the
  // planet's body frame, so cdir's latitude is the planet's latitude — the bands sit still.
  vec2 weatherUV(vec3 d){
    float lon = atan(d.z, d.x);
    float lat = asin(clamp(d.y, -1.0, 1.0));
    return vec2((lon + PI) / (2.0 * PI), 0.5 - lat / PI);
  }

  // Full cloud density at a local point: banked coverage × a per-column height profile
  // whose TOP is modulated by the detail field (reused — no extra fBm) so heights vary.
  float densityAt(vec3 p){
    float r = length(p);
    float alt = clamp((r - uInnerR) / (uOuterR - uInnerR), 0.0, 1.0);
    vec3 cdir = p / r;
    // Fine STATIC texture (crisp edges + per-column height variation). No cloudFlow drift here —
    // the large-scale MOTION now comes from the baked wind-advected loop, not a fake spin.
    float detail = cfbm(cdir * uFreq * 2.4 + 19.0);
    float degrid = cfbm(cdir * uFreq * 1.3 + 4.0);
    float cov;
    if (uHasWeather > 0.5) {
      // Baked, MOVING cloud coverage: the wind-advected loop (see server climate/wind.go), replayed
      // by the CPU into this texture. It carries BOTH where cloud is (the ITCZ / substellar ring)
      // and how it MOVES — super-rotation carrying the ring on a tidally-locked world, geostrophic
      // jets on a rotator. The cloud SHAPES in the baked field genuinely translate, so this reads
      // as flowing cloud; the static detail only crisps the edges. De-grid the coarse cells with the
      // procedural taps so they don't read as blocks.
      vec2 uv = weatherUV(cdir) + vec2(degrid - 0.5, detail - 0.5) * 0.03;
      float bk = texture2D(uWeather, uv).r;
      cov = smoothstep(0.30, 0.62, bk) * (0.55 + 0.5 * detail);
    } else {
      // Pre-load fallback (the ~second before the baked loop arrives, and decks with no weather):
      // the procedural coverage gated by the analytic hot/dry term.
      vec3 wdir = cloudFlow(cdir);
      float base = cfbm(wdir * uFreq);
      cov = smoothstep(uCoverLo, uCoverHi, base - (1.0 - detail) * 0.22);
      cov *= 1.0 - uClearOnHeat * climateBake(climateTempC(cdir.y, dot(cdir, uSunLocalDir)));
    }
    if (cov <= 0.001) return 0.0;
    // Tall where the bank is dense AND the detail field peaks; thin wisps run low.
    float top = clamp(mix(0.16, 1.0, detail) * smoothstep(0.04, 0.55, cov), 0.06, 1.0);
    return cov * vProfile(alt, top);
  }

  // Ray vs sphere (centred at origin); returns the two intersection distances.
  bool raySphere(vec3 ro, vec3 rd, float R, out float t0, out float t1){
    float b = dot(ro, rd);
    float c = dot(ro, ro) - R * R;
    float h = b * b - c;
    if (h < 0.0) { t0 = 0.0; t1 = 0.0; return false; }
    h = sqrt(h);
    t0 = -b - h; t1 = -b + h;
    return true;
  }

  void main(){
    // March in the deck's local frame (where the noise lives, so clouds ride with the
    // planet). The ray enters the outer shell; if it would hit the planet (inner
    // sphere) we stop there, otherwise it grazes the limb and crosses the whole chord.
    vec3 ro = uCamLocal;
    vec3 rd = normalize(vLocalPos - ro);
    float to0, to1, ti0, ti1;
    if (!raySphere(ro, rd, uOuterR, to0, to1)) discard;
    float tStart = max(to0, 0.0);
    float tEnd = to1;
    if (raySphere(ro, rd, uInnerR, ti0, ti1) && ti0 > tStart) tEnd = ti0;
    if (tEnd <= tStart) discard;

    vec3 sunL = normalize(uSunLocalDir);
    float shell = max(uOuterR - uInnerR, 1e-5);
    float invShell = 1.0 / shell;          // optical depth ∝ path / shell thickness, so
                                           // extinction is scale-independent and the long
                                           // grazing limb path piles up a solid edge.
    float dt = (tEnd - tStart) / float(STEPS);
    // Offset the first sample by INTERLEAVED GRADIENT NOISE (not white noise): it
    // de-bands the march like a random jitter but its low-discrepancy pattern reads as
    // a faint even texture instead of the ugly per-pixel speckle white noise leaves on
    // the thin, semi-transparent limb. Stable per pixel, so it doesn't crawl either.
    float ign = fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
    float t = tStart + dt * ign;
    // Forward-scatter silver lining: looking toward the sun through cloud brightens it.
    float fwd = pow(max(dot(rd, sunL), 0.0), 3.0);

    float trans = 1.0;
    vec3 lum = vec3(0.0);
    for (int i = 0; i < STEPS; i++) {
      vec3 p = ro + rd * t;
      float dens = densityAt(p);
      if (dens > 0.003) {
        vec3 N = normalize(p);
        float ndl = max(dot(N, sunL), 0.0);
        float alt = clamp((length(p) - uInnerR) / (uOuterR - uInnerR), 0.0, 1.0);
        // Cheap self-shadow proxy (no extra noise tap): deeper in the deck is more
        // occluded, so cloud TOPS read bright and the base shades down. Plus the sunlit
        // term, a forward-scatter silver lining, and a little ambient.
        float occ = mix(0.5, 1.15, alt);
        // Sunlit cloud TOPS read white (lit term >=1 tone-maps to white) while shaded
        // undersides/valleys stay darker — keeping the contrast that shows the swirl detail.
        // (Pushing the floor + ambient too high flattens it to a blown-out white blob.)
        float lit = (0.18 + 0.82 * ndl) * occ + 0.5 * fwd * occ + 0.1;
        float a = 1.0 - exp(-dens * uExtinction * dt * invShell);
        lum += trans * a * uColor * lit;
        trans *= 1.0 - a;
        if (trans < 0.02) break;
      }
      t += dt;
    }

    float outA = (1.0 - trans) * uOpacity;
    if (outA < 0.004) discard;
    vec3 outCol = lum / max(1.0 - trans, 1e-4);
    // Fade the deck out on the deep night side (don't show a grey veil where the sun
    // doesn't reach), and dim through a moon's umbra (shared eclipse term).
    vec3 midDir = normalize(ro + rd * (tStart + tEnd) * 0.5);
    float dayNight = smoothstep(-0.32, 0.05, dot(midDir, sunL));
    outA *= mix(0.12, 1.0, dayNight);
    // Darken the cloud COLOUR (not just alpha) toward black on the unlit hemisphere: the lighting
    // model keeps an ambient floor so a sunless cloud still reads grey, which on a tidally-locked
    // world's permanent night face glows wrongly. Fade colour to near-black there so night clouds
    // sit dark against the unlit surface, while day/terminator clouds keep their lit tops.
    outCol *= mix(0.04, 1.0, dayNight);
    outCol *= mix(0.3, 1.0, sunVisibility(vWorldPos));
    gl_FragColor = vec4(outCol, outA);
  }
`;

interface Props {
  planetRadius: number;
  sunWorldPos: Vector3;
  sunRadius: number;
  occluders: Occluder[];
  seed: number;
  /** Cloud-deck tint (white Earth-like, yellow-green toxic, dusty tan arid…). */
  color?: string;
  /** Fraction of the disc under cloud (0 = a few wisps, 1 = fully shrouded). */
  coverage?: number;
  /** Per-fragment climate (day/night/mean temps, tilt): the deck clears over the
   *  scorched substellar / desert cells so cloud follows the moist temperate air. */
  climate: SurfaceClimate;
  /** 1 for a tidally-locked world (selects the substellar temperature field). */
  tidalLock: number;
  /** The shared seasonal cloud-cover texture (built + interpolated by Planet, sampled by BOTH
   *  the deck and its shadows so they track exactly). uHasWeather gates its use. */
  weatherTex: DataTexture;
  /** Whether a baked weather video is loaded (else the analytic hot/dry gate applies). */
  hasWeather: boolean;
}

// mulberry32 — deterministic PRNG so a planet's seed yields stable drift parameters.
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Vortex {
  center: [number, number, number];
  data: [number, number, number]; // angular radius, spiral twist, spin rate
}

interface CloudParams {
  axis: [number, number, number];
  speed: number;
  phase: number;
  baseFlow: number;
  bandCount: number;
  bandPhase: number;
  vortices: Vortex[];
}

// A uniform random point on the unit sphere from two [0,1) draws.
function spherePoint(u: number, v: number, zScale = 1): [number, number, number] {
  const theta = 2 * Math.PI * u;
  const z = (2 * v - 1) * zScale;
  const s = Math.sqrt(Math.max(0, 1 - z * z));
  return [s * Math.cos(theta), z, s * Math.sin(theta)];
}

// Per-planet flow field: spin axis, zonal belts, and standing storm vortices — all
// seed-derived so a planet always flows the same way and no two flow alike.
function cloudParams(seed: number): CloudParams {
  const r = mulberry32(seed);
  const axis = spherePoint(r(), r());
  const speed = (0.006 + r() * 0.014) * (r() < 0.5 ? -1 : 1);
  const phase = r() * 100;
  const baseFlow = 0.3 + r() * 0.5; // net prograde jet so belts don't cancel out
  const bandCount = 3 + Math.floor(r() * 4); // 3–6 alternating belts
  const bandPhase = r() * Math.PI * 2;
  const vortices: Vortex[] = [];
  for (let i = 0; i < NVORTEX; i++) {
    const center = spherePoint(r(), r(), 0.85);
    const r0 = 0.16 + r() * 0.18;
    const twist = (0.5 + r() * 0.9) * (r() < 0.5 ? -1 : 1);
    const spin = (0.01 + r() * 0.025) * (r() < 0.5 ? -1 : 1);
    vortices.push({ center, data: [r0, twist, spin] });
  }
  return { axis, speed, phase, baseFlow, bandCount, bandPhase, vortices };
}

export function Clouds({
  planetRadius,
  sunWorldPos,
  sunRadius,
  occluders,
  seed,
  color = '#ffffff',
  coverage = 0.5,
  climate,
  tidalLock,
  weatherTex,
  hasWeather,
}: Props) {
  const meshRef = useRef<Mesh>(null);
  const startTime = useRef<number | null>(null);
  const sunLocal = useMemo(() => new Vector3(), []);
  const camLocal = useMemo(() => new Vector3(), []);
  const camera = useThree((s) => s.camera);
  const shadow = useMemo(makeShadowUniforms, []);

  const uniforms = useMemo(() => {
    const p = cloudParams(seed);
    // Coverage threshold band + optical density: a dry world shows a few scattered banks, a toxic
    // world is socked in. Thresholds come from the SHARED cloudThresholds (which applies
    // CLOUD_COVERAGE_SCALE) so the deck and its shadows classify identical cloud; extinction uses
    // the same scaled cover so the two never drift apart.
    const cov = Math.max(0, Math.min(1, coverage)) * CLOUD_COVERAGE_SCALE;
    const { coverLo, coverHi } = cloudThresholds(coverage);
    const extinction = 8 + 12 * cov;
    return {
      uTime: { value: 0 },
      uColor: { value: new Color(color) },
      uCoverLo: { value: coverLo },
      uCoverHi: { value: coverHi },
      uFreq: { value: 3.8 },
      uExtinction: { value: extinction },
      uOpacity: { value: 1.0 },
      uSunLocalDir: { value: new Vector3(0, 0, 1) },
      uCamLocal: { value: new Vector3() },
      uInnerR: { value: planetRadius * INNER_SCALE },
      uOuterR: { value: planetRadius * OUTER_SCALE },
      uDriftAxis: { value: new Vector3(...p.axis) },
      uDriftSpeed: { value: p.speed },
      uPhase: { value: p.phase },
      uBaseFlow: { value: p.baseFlow },
      uBandCount: { value: p.bandCount },
      uBandPhase: { value: p.bandPhase },
      uVortexCenter: { value: p.vortices.map((v) => new Vector3(...v.center)) },
      uVortexData: { value: p.vortices.map((v) => new Vector3(...v.data)) },
      uMeanT: { value: climate.meanTempC },
      uDayT: { value: climate.dayTempC },
      uNightT: { value: climate.nightTempC },
      uObliq: { value: climate.obliquity },
      uTidalLock: { value: tidalLock },
      uClearOnHeat: { value: climate.canScorch ? 1 : 0 },
      uWeather: { value: weatherTex },
      uHasWeather: { value: hasWeather ? 1 : 0 },
      ...shadow,
    };
  }, [shadow, seed, color, coverage, planetRadius, climate, tidalLock, weatherTex, hasWeather]);

  useFrame(() => {
    if (!meshRef.current) {
      return;
    }
    // Drive cloud motion off the game clock (so it speeds up with the sim-speed
    // control), measured from the first frame after focus so the flow winds up from a
    // neutral state rather than snapping in pre-sheared.
    if (startTime.current === null) {
      startTime.current = gameClock.time;
    }
    uniforms.uTime.value = gameClock.time - startTime.current;
    // Sun direction + camera position in the deck's local frame (the mesh isn't spun,
    // so the noise/march live here). worldToLocal folds in the parent's apparentScale,
    // so uInnerR/uOuterR stay in geometry units.
    meshRef.current.worldToLocal(sunLocal.copy(sunWorldPos));
    uniforms.uSunLocalDir.value.copy(sunLocal).normalize();
    meshRef.current.worldToLocal(camLocal.copy(camera.position));
    uniforms.uCamLocal.value.copy(camLocal);
    setOccluders(shadow, sunWorldPos, sunRadius, occluders);
  });

  return (
    <mesh ref={meshRef} renderOrder={2}>
      <sphereGeometry args={[planetRadius * OUTER_SCALE, 48, 32]} />
      <shaderMaterial
        vertexShader={vert}
        fragmentShader={frag}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        toneMapped={false}
      />
    </mesh>
  );
}
